import { useState, useEffect, useRef } from 'react';

const VERIFY_URL = '/api/host-context';
const OFFLINE_DEBOUNCE_MS = 4000;
const ONLINE_VERIFY_MS = 1500;

async function probeNetwork() {
  if (!navigator.onLine) return false;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch(VERIFY_URL, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    return res.ok || res.status === 401 || res.status === 404;
  } catch {
    return false;
  }
}

/**
 * Reliable connectivity for UI banners — avoids false positives from navigator.onLine alone.
 */
export default function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const offlineTimer = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    const markOnline = async () => {
      if (offlineTimer.current) {
        clearTimeout(offlineTimer.current);
        offlineTimer.current = null;
      }
      const ok = await probeNetwork();
      if (mounted.current) setIsOnline(ok);
    };

    const markOffline = () => {
      if (offlineTimer.current) clearTimeout(offlineTimer.current);
      offlineTimer.current = setTimeout(async () => {
        const ok = await probeNetwork();
        if (mounted.current) setIsOnline(ok);
      }, OFFLINE_DEBOUNCE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') markOnline();
    };

    window.addEventListener('online', markOnline);
    window.addEventListener('offline', markOffline);
    document.addEventListener('visibilitychange', onVisibility);

    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') markOnline();
    }, ONLINE_VERIFY_MS * 4);

    markOnline();

    return () => {
      mounted.current = false;
      if (offlineTimer.current) clearTimeout(offlineTimer.current);
      window.removeEventListener('online', markOnline);
      window.removeEventListener('offline', markOffline);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(iv);
    };
  }, []);

  return isOnline;
}